import type { Email, ThreadMessage } from '@/types/email';
import type { ReplyContext } from '@/types/ui';
import type { ReplyMode } from '../components/MessageMetadata';
import { fetchEmail } from '@/api/emails';
import { formatAddressChip } from '@/lib/addressParsing';

type EmailLike = Email | ThreadMessage;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\n/g, '<br>');
}

interface BuildReplyContextOptions {
  sanitizedHtml?: string | null;
  /** Set of user's own email addresses (lowercase) to exclude from CC on Reply All */
  accountEmails?: Set<string>;
}

export function buildReplyContext(
  email: EmailLike,
  modeType: ReplyMode,
  options: BuildReplyContextOptions = {}
): ReplyContext {
  const { sanitizedHtml, accountEmails } = options;
  const replyBodyHtml = sanitizedHtml ?? email.bodyHtml ?? (email.body ? escapeHtml(email.body) : undefined);
  const replyTarget = email.replyTo && email.replyTo.length > 0 ? email.replyTo[0] : email.from;

  const originalToList = email.to.map((r) => {
    const name = r.name?.trim();
    return name ? `${name} <${r.email}>` : r.email;
  }).join(', ');

  const baseContext: ReplyContext = {
    // The reply MUST come from the account that received the message —
    // ComposeModalHost/initializeNewDraft prefer this over the selected
    // account (which is 'all' in the unified view) (G1).
    accountId: email.accountId ?? null,
    to: modeType === 'forward' ? '' : replyTarget.email,
    subject: email.subject,
    body: replyBodyHtml,
    mode: modeType,
    cc: undefined,
    threadId: email.threadId,
    messageId: email.messageId ?? null,
    references: email.references ?? null,
    fromName: email.from.name ?? undefined,
    fromEmail: email.from.email,
    date: email.date.toISOString(),
    originalTo: originalToList || undefined,
    // Forward carries the original real (non-inline) attachments so the
    // forwarded message actually includes the files (audit §2.9 #5). Inline body
    // images are excluded — they're already part of the quoted HTML.
    attachments:
      modeType === 'forward'
        ? (email.attachments ?? []).filter((att) => !att.inline)
        : undefined,
    sourceEmailId: modeType === 'forward' ? email.id : undefined,
  };

  if (modeType === 'replyAll') {
    // formatAddressChip RFC-quotes names containing separators — a bare
    // `Name <email>` serialization re-split "Doe, John" into two chips (G10).
    const formatRecipient = (r: { email: string; name?: string | null }) => {
      const name = r.name?.trim();
      return name && name !== r.email ? formatAddressChip(name, r.email) : r.email;
    };

    const excludeSet = new Set<string>();
    const replyToAddress = replyTarget.email?.toLowerCase?.() ?? '';
    const fromAddress = email.from.email?.toLowerCase?.() ?? '';
    if (replyToAddress) excludeSet.add(replyToAddress);
    if (fromAddress) excludeSet.add(fromAddress);
    if (accountEmails) {
      accountEmails.forEach((userEmail) => excludeSet.add(userEmail));
    }

    const shouldInclude = (email: string) => !excludeSet.has(email.toLowerCase());

    // Original To recipients (minus sender/self) stay in To
    const additionalTo = new Map<string, string>();
    email.to.forEach((recipient) => {
      const address = recipient.email?.trim();
      if (!address || !shouldInclude(address)) return;
      const key = address.toLowerCase();
      if (!additionalTo.has(key)) {
        additionalTo.set(key, formatRecipient(recipient));
      }
    });

    if (additionalTo.size > 0) {
      baseContext.to = [baseContext.to, ...additionalTo.values()].filter(Boolean).join(', ');
    }

    // Original CC recipients (minus sender/self/already-in-To) stay in CC
    const ccRecipients = new Map<string, string>();
    (email.cc ?? []).forEach((recipient) => {
      const address = recipient.email?.trim();
      if (!address || !shouldInclude(address)) return;
      const key = address.toLowerCase();
      if (!additionalTo.has(key) && !ccRecipients.has(key)) {
        ccRecipients.set(key, formatRecipient(recipient));
      }
    });

    if (ccRecipients.size > 0) {
      baseContext.cc = Array.from(ccRecipients.values()).join(', ');
    }
  }

  return baseContext;
}

/**
 * Like {@link buildReplyContext}, but when the resolved reply target address is
 * empty — a list row with an empty `from_address` and no Reply-To — fetch the
 * full message detail first. The detail endpoint parses the raw From/Reply-To
 * headers, so it can recover a usable address the list projection dropped. Falls
 * back to the original email (blank To) if the fetch fails or yields no address.
 *
 * Reply/Reply-All only — Forward has no reply target, so it skips the fetch.
 */
export async function buildReplyContextWithFallback(
  email: EmailLike,
  modeType: ReplyMode,
  options: BuildReplyContextOptions = {}
): Promise<ReplyContext> {
  const target = email.replyTo && email.replyTo.length > 0 ? email.replyTo[0] : email.from;
  const targetEmail = target?.email?.trim() ?? '';
  if (modeType === 'forward' || targetEmail || !email.id || !email.accountId) {
    return buildReplyContext(email, modeType, options);
  }
  try {
    const detail = await fetchEmail(email.accountId, email.id, email.folderId ?? undefined);
    const detailTarget =
      detail.replyTo && detail.replyTo.length > 0 ? detail.replyTo[0] : detail.from;
    if (detailTarget?.email?.trim()) {
      // Keep the caller's sanitizedHtml/accountEmails; the recovered headers come
      // from the detail. sanitizedHtml is for the same message, so it stays valid.
      return buildReplyContext(detail, modeType, options);
    }
  } catch {
    // Fall through to the original (blank-To) context rather than failing the reply.
  }
  return buildReplyContext(email, modeType, options);
}
